/**
 * Module de rendu du zodiaque et des décans
 */
import { wheelAngle } from '../core/utils.js';

export class ZodiacRenderer {
    /**
     * @param {CanvasRenderingContext2D} ctx - Contexte 2D du canvas
     * @param {Object} options - Options de configuration
     */
    constructor(ctx, options) {
        this.ctx = ctx;
        this.options = options;
    }

    /**
     * Dessine les signes du zodiaque - Style Art Déco
     * @param {number} cx - Coordonnée X du centre
     * @param {number} cy - Coordonnée Y du centre
     * @param {number} radius - Rayon du cercle extérieur
     */
    drawZodiac(cx, cy, radius) {
        const ctx = this.ctx;
        const zodiacInnerRadius = radius * (1 - this.options.zodiacWidth);

        // Dessiner les 12 séparations avec style Art Déco
        for (let i = 0; i < 12; i++) {
            const angle = wheelAngle(i * 30, this.options.ascendant);

            ctx.beginPath();
            ctx.moveTo(
                cx + zodiacInnerRadius * Math.cos(angle),
                cy - zodiacInnerRadius * Math.sin(angle)
            );
            ctx.lineTo(
                cx + radius * Math.cos(angle),
                cy - radius * Math.sin(angle)
            );
            ctx.strokeStyle = 'rgba(230, 191, 164, 0.7)';  // line-strong, softened
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Colorer alternativement les signes avec tons Art Déco
            if (i % 2 === 0) {
                // Adapter les angles de début et de fin
                // ctx.arc measures its angles the screen's way (y down): the opposite of wheelAngle.
                const startAngle = -wheelAngle(i * 30, this.options.ascendant);
                const endAngle = -wheelAngle((i + 1) * 30, this.options.ascendant);

                ctx.beginPath();
                ctx.arc(cx, cy, radius, startAngle, endAngle, true);
                ctx.arc(cx, cy, zodiacInnerRadius, endAngle, startAngle, false);
                ctx.closePath();
                // Dégradé doré subtil Art Déco
                const gradient = ctx.createRadialGradient(cx, cy, zodiacInnerRadius, cx, cy, radius);
                gradient.addColorStop(0, 'rgba(242, 121, 61, 0.05)');  // fox, a breath of it
                gradient.addColorStop(1, 'rgba(255, 198, 92, 0.11)'); // honey
                ctx.fillStyle = gradient;
                ctx.fill();
            }
        }

        // Les douze signes, chacun suivi de U+FE0E — le sélecteur de variante TEXTE.
        //
        // Sans lui, ils ne sont pas du texte pour le navigateur : U+2648..U+2653 ont
        // Emoji_Presentation=Yes, donc Chrome les rend avec une police EMOJI, en couleur, et
        // `fillStyle` n'a plus aucune prise. La roue posait douze pastilles violettes sur une
        // page crème — mesuré en capture, invisible autrement. Les glyphes de planètes
        // (☉ ☽ ☿ ♀ ♂ …) n'ont pas ce défaut par défaut : eux se peignent déjà.
        const TEXT_PRESENTATION = '︎';
        const symbols = ['♈', '♉', '♊', '♋', '♌', '♍', '♎', '♏', '♐', '♑', '♒', '♓']
            .map((sign) => sign + TEXT_PRESENTATION);

        for (let i = 0; i < 12; i++) {
            // Positionner correctement les symboles
            const angle = wheelAngle(i * 30 + 15, this.options.ascendant); // 15 = milieu du signe
            const symbolRadius = radius - (radius - zodiacInnerRadius) / 2;

            // Taille de la police ajustée avec style Art Déco
            const fontSize = Math.max(20, Math.round(radius * 0.07));
            ctx.font = `${fontSize}px "Noto Sans Symbols", sans-serif`;
            ctx.fillStyle = '#d85f28';  // fox, deep — the glyphs are what one reads here
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';

            // Ombre dorée subtile pour effet Art Déco
            ctx.shadowColor = 'rgba(255, 198, 92, 0.35)';  // honey
            ctx.shadowBlur = 2;
            ctx.shadowOffsetX = 1;
            ctx.shadowOffsetY = 1;

            ctx.fillText(
                symbols[i],
                cx + symbolRadius * Math.cos(angle),
                cy - symbolRadius * Math.sin(angle)
            );

            // Réinitialiser l'ombre
            ctx.shadowColor = 'transparent';
            ctx.shadowBlur = 0;
            ctx.shadowOffsetX = 0;
            ctx.shadowOffsetY = 0;
        }
    }

    /**
     * Dessine les décans (divisions des signes en 3) - Style Art Déco
     * @param {number} cx - Coordonnée X du centre
     * @param {number} cy - Coordonnée Y du centre
     * @param {number} radius - Rayon du cercle extérieur
     */
    drawDecans(cx, cy, radius) {
        const ctx = this.ctx;
        const zodiacInnerRadius = radius * (1 - this.options.zodiacWidth);

        // Dessiner les 36 lignes de décans (12 signes × 3 décans)
        for (let i = 0; i < 36; i++) {
            // Sauter les positions qui correspondent déjà aux lignes des signes (tous les 30°)
            if (i % 3 === 0) continue;

            // Position en degrés (chaque décan = 10°)
            const position = i * 10;
            const angle = wheelAngle(position, this.options.ascendant);

            // Ligne plus fine avec ton doré Art Déco
            ctx.beginPath();
            ctx.moveTo(
                cx + zodiacInnerRadius * Math.cos(angle),
                cy - zodiacInnerRadius * Math.sin(angle)
            );
            ctx.lineTo(
                cx + radius * Math.cos(angle),
                cy - radius * Math.sin(angle)
            );
            ctx.strokeStyle = 'rgba(230, 191, 164, 0.35)'; // line-strong, faint
            ctx.lineWidth = 0.8; // Plus fin que les lignes des signes
            ctx.stroke();
        }
    }
}